import { pubsub } from '@services';
import { notyf } from '@modules';

class QuantityInput extends HTMLElement {
  /** @type {HTMLInputElement} */ #input = this.querySelector('input');
  /** @type {NodeListOf<HTMLButtonElement>} */ #buttons = this.querySelectorAll('button');
  /** @type {HTMLButtonElement} */ #removeButton = this.querySelector('[data-line-item-remove]');
  /** @type {boolean} */ #isBundle = this.hasAttribute('data-bundle-quantity');

  connectedCallback() {
    this.#toggleClasses();
    this.updateButtonStates();
    this.#toggleRemoveButton();

    this.#buttons.forEach((button) => {
      button.addEventListener('click', this.#handleStep);
    });

    this.#input.addEventListener('change', this.#onChange);
  }

  disconnectedCallback() {
    this.#input.removeEventListener('change', this.#onChange);
    this.#buttons.forEach((button) => {
      button.removeEventListener('click', this.#handleStep);
    });
  }

  #onChange = () => {
    this.#constrainAndUpdateInput(this.#input.value);
    this.#toggleClasses();
    if (this.#removeButton) this.#toggleRemoveButton();
    this.#publishQuantityChange();
  };

  #toggleClasses() {
    this.classList.toggle('has-value', parseInt(this.#input.value) > 0);
  }

  /**
   * Handles a step button click (plus or minus).
   * Updates the input value, constrains it, and publishes a quantity change if the value changed.
   * @param {Event} event - The event object
   */
  #handleStep = (event) => {
    const type = event.currentTarget.name;
    const value = parseInt(this.#input.value);
    const step = parseInt(this.#input.step) || 1;
    const newValue = type === 'minus' ? Math.max(value - step, 0) : value + step;
    const updatedValue = this.#constrainAndUpdateInput(newValue);

    if (parseInt(updatedValue) !== parseInt(value)) this.#publishQuantityChange();
  };

  /**
   * Applies min/max constraints to the value, updates the input, and refreshes button states.
   * Shows a Notyf error when the value exceeds max.
   * @param {number|string} newValue - The new value to set
   * @returns {string} The constrained value as stored in the input
   */
  // eslint-disable-next-line complexity
  #constrainAndUpdateInput(newValue) {
    const value = parseInt(newValue);
    const min = parseInt(this.#input.min) || 0;
    const max = parseInt(this.#input.max) || Infinity;
    this.#input.value = value;

    if (value >= max) {
      const errorMessage = window.toast_translations?.product?.quantity?.error_max?.replace(
        '{{ max }}',
        max
      );
      if (errorMessage && !this.#isBundle) {
        notyf.error(errorMessage);
      }
      this.#input.value = parseInt(max);
    } else if (value <= min) {
      this.#input.value = parseInt(min);
    }

    this.#toggleClasses();
    this.updateButtonStates();
    return this.#input.value;
  }

  /**
   * Publishes a `quantity:change` event via PubSub with the current line index and quantity.
   * For bundle quantity inputs, also publishes a `bundle:quantity:change` event.
   */
  #publishQuantityChange() {
    pubsub.publish('quantity:change', {
      line: this.dataset?.itemIndex,
      quantity: parseInt(this.#input.value),
    });

    if (this.#isBundle) {
      pubsub.publish('bundle:quantity:change', {
        variantId: parseInt(this.dataset.variantId),
        quantity: parseInt(this.#input.value),
      });
    }
  }

  /**
   * Enables or disables plus/minus buttons based on current value and min/max.
   */
  updateButtonStates() {
    const { min, max, value } = this.#input;
    const minusButton = [...this.#buttons]?.find((button) => button.name === 'minus');
    const plusButton = [...this.#buttons]?.find((button) => button.name === 'plus');

    if (plusButton && max) {
      // Disable the plus button if the quantity reaches the maximum
      plusButton.disabled = parseInt(value) >= parseInt(max);
    }
    if (minusButton && min) {
      // Disable the minus button if the quantity reaches the minimum
      minusButton.disabled = parseInt(value) <= parseInt(min);
    }
  }

  /**
   * Toggles the visibility of the remove button based on the current quantity.
   */
  #toggleRemoveButton() {
    if (!this.#removeButton) return;

    const quantityAboveOne = parseInt(this.#input.value) > 1;
    this.#removeButton?.classList.toggle('hidden', quantityAboveOne);
    const minusButton = [...this.#buttons]?.find((button) => button.name === 'minus');
    minusButton?.classList.toggle('hidden', !quantityAboveOne);
  }
}

if (!customElements.get('quantity-input')) {
  customElements.define('quantity-input', QuantityInput);
}
